import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Bell, BellOff, AlertTriangle, Tag, Gift, Recycle, Trash2 } from "lucide-react";
import { TYPE_META } from "@/pages/MarketplacePage";
import { labelCat, labelSub, labelItem } from "@/lib/marketplaceLabels";

const LISTING_STATE_META = {
  published: { label: "Attivo", cls: "bg-emerald-100 text-emerald-800" },
  pending_confirmation: { label: "In attesa di conferma", cls: "bg-amber-100 text-amber-800" },
  closed: { label: "Concluso", cls: "bg-slate-200 text-slate-700" },
};

const EVENT_STATE_META = {
  published: { label: "In corso", cls: "bg-red-100 text-red-800" },
  pending_moderation: { label: "In moderazione", cls: "bg-yellow-100 text-yellow-800" },
  concluded: { label: "Concluso", cls: "bg-slate-200 text-slate-700" },
  rejected: { label: "Non pubblicato", cls: "bg-slate-200 text-slate-700" },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

export default function Following() {
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [tab, setTab] = useState("listings"); // listings | events
  const [events, setEvents] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (!authLoading && !user) nav("/auth?mode=signin"); }, [user, authLoading, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/following");
      setEvents(data.events || []);
      setListings(data.listings || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const unfollowListing = async (lid) => {
    try {
      const { data } = await api.post(`/marketplace/listings/${lid}/follow`, {});
      if (!data.following) {
        setListings((arr) => arr.filter((l) => l.listing_id !== lid));
        toast.success("Rimosso dai Seguiti");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const unfollowEvent = async (eid) => {
    try {
      const { data } = await api.post(`/events/${eid}/follow`, {});
      if (!data.following) {
        setEvents((arr) => arr.filter((e) => e.event_id !== eid));
        toast.success("Rimosso dai Seguiti");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const counts = useMemo(() => ({ listings: listings.length, events: events.length }), [listings, events]);

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="following-page">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-6 pb-4"><BackButton /></div>
      <div className="max-w-5xl mx-auto px-4 md:px-8 pb-10">
        <div className="text-xs uppercase tracking-[0.25em] text-slate-600 font-semibold mb-1">Sezione personale</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-1" data-testid="following-heading">Seguiti</h1>
        <p className="text-sm text-slate-600 mb-5 max-w-2xl">
          Tutto ciò che segui in un unico posto: annunci del Marketplace ed Eventi Straordinari.
          Gli elementi conclusi restano visibili con lo stato aggiornato: puoi rimuoverli manualmente quando vuoi.
        </p>

        <div className="flex gap-2 flex-wrap mb-4" role="tablist">
          <button
            data-testid="following-tab-listings"
            onClick={() => setTab("listings")}
            className={`h-9 px-4 rounded-full text-xs font-semibold border ${tab === "listings" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
          >
            Annunci <span className="ml-1 opacity-70">{counts.listings}</span>
          </button>
          <button
            data-testid="following-tab-events"
            onClick={() => setTab("events")}
            className={`h-9 px-4 rounded-full text-xs font-semibold border ${tab === "events" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
          >
            Eventi straordinari <span className="ml-1 opacity-70">{counts.events}</span>
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-slate-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Caricamento…</div>
        ) : tab === "listings" ? (
          listings.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500 italic" data-testid="following-empty-listings">
              Non stai ancora seguendo nessun annuncio. Apri un annuncio del Marketplace e premi «Segui».
            </div>
          ) : (
            <div className="space-y-2" data-testid="following-listings">
              {listings.map((l) => {
                const M = TYPE_META[l.type] || TYPE_META.gift;
                const S = LISTING_STATE_META[l.state] || LISTING_STATE_META.published;
                const Icon = M.icon;
                return (
                  <div key={l.listing_id} data-testid={`following-row-${l.listing_id}`} className={`bg-white border-2 ${M.ring} rounded-2xl p-4 flex flex-wrap gap-3 items-start`}>
                    {l.photos && l.photos[0] ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${l.photos[0]}`} alt="" className="w-20 h-20 object-cover rounded-xl" />
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-slate-100 flex items-center justify-center text-slate-300"><Icon size={22} /></div>
                    )}
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <Badge style={{ background: M.color, color: "#fff" }} className="inline-flex items-center gap-1"><Icon size={11} />{t(`mk_type_${l.type}`)}</Badge>
                        <Badge className={S.cls} data-testid={`following-state-${l.listing_id}`}>{l.state === "closed" ? "Non più disponibile" : S.label}</Badge>
                        <Badge variant="outline" className="text-[10px]">
                          {labelCat(l.category, lang)} → {labelSub(l.subcategory, lang)} → {labelItem(l.item_type, lang)}
                        </Badge>
                      </div>
                      <Link to={`/marketplace/${l.listing_id}`} className="font-semibold text-slate-900 hover:underline block truncate">{l.title}</Link>
                      {l.type === "sale" && l.price != null && (
                        <div className="text-sm text-purple-700 font-bold">€ {l.price}</div>
                      )}
                      <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-3">
                        <span>Pubblicato: {formatDate(l.created_at)}</span>
                        {l.state === "closed" && <span>Concluso: {formatDate(l.closed_at)}</span>}
                        {l.province_name && <span>{l.province_name}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Link to={`/marketplace/${l.listing_id}`} className="h-9 px-3 rounded-full text-xs font-semibold bg-slate-900 text-white inline-flex items-center gap-1 hover:bg-slate-800">Apri</Link>
                      <button
                        data-testid={`following-unfollow-${l.listing_id}`}
                        onClick={() => unfollowListing(l.listing_id)}
                        className="h-9 px-3 rounded-full text-xs font-semibold bg-white text-red-700 border border-red-200 hover:bg-red-50 inline-flex items-center gap-1"
                      >
                        <BellOff size={12} /> {l.state === "closed" ? <span className="inline-flex items-center gap-1"><Trash2 size={12} /> Rimuovi</span> : "Smetti di seguire"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          events.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500 italic" data-testid="following-empty-events">
              Non stai ancora seguendo nessun evento straordinario.
            </div>
          ) : (
            <div className="space-y-2" data-testid="following-events">
              {events.map((e) => {
                const S = EVENT_STATE_META[e.state] || EVENT_STATE_META.published;
                return (
                  <div key={e.event_id} data-testid={`following-event-${e.event_id}`} className="bg-white border-2 border-red-200 rounded-2xl p-4 flex flex-wrap gap-3 items-start">
                    <div className="w-11 h-11 rounded-full bg-red-600 text-white flex items-center justify-center shrink-0"><AlertTriangle size={20} /></div>
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <Badge className={S.cls}>{S.label}</Badge>
                        <Badge variant="outline" className="text-[10px]">{e.kind}</Badge>
                      </div>
                      <Link to={`/events/${e.event_id}`} className="font-semibold text-slate-900 hover:underline block truncate">{e.title || e.kind}</Link>
                      <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-3">
                        <span>Aperto: {formatDate(e.created_at)}</span>
                        {e.province_name && <span>{e.province_name}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Link to={`/events/${e.event_id}`} className="h-9 px-3 rounded-full text-xs font-semibold bg-slate-900 text-white inline-flex items-center gap-1 hover:bg-slate-800">Apri</Link>
                      <button
                        data-testid={`following-unfollow-event-${e.event_id}`}
                        onClick={() => unfollowEvent(e.event_id)}
                        className="h-9 px-3 rounded-full text-xs font-semibold bg-white text-red-700 border border-red-200 hover:bg-red-50 inline-flex items-center gap-1"
                      >
                        <BellOff size={12} /> Smetti di seguire
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}
